import { useCallback, useEffect, useRef, useState } from "react"
import { router } from "@inertiajs/react"
import { Loader2, Send, Sparkles, RefreshCw, Trash2, Eraser } from "lucide-react"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { consumeSSE } from "@/lib/sse"
import { ProposalDiffCard, type Proposal } from "@/components/domain/proposal-diff-card"
import { Badge } from "@/components/ui/badge"
import { ChatMessageBubble } from "@/components/domain/chat-message-bubble"
import { ChatThinkingIndicator } from "@/components/domain/chat-thinking-indicator"
import { FeedbackButton } from "@/components/domain/feedback-button"
import { SkillSetupBody } from "@/components/domain/skill-setup-panel"
import { cn } from "@/lib/cn"
import type { ChatBubble } from "@/types/test_lab"

// Designer messages mirror the ChatBubble shape so they can render
// through the same ChatMessageBubble component the Test pane uses. The
// id is a synthetic counter (server doesn't persist these chats) and
// createdAt is captured at message-creation time.
type Message = {
  id:        number
  role:      "user" | "assistant"
  content:   string
  createdAt: string
}

type Props = {
  skillId: number
  /** Current persisted skill snapshot — diff cards render `before` slots from this. */
  skillSnapshot: Record<string, unknown>
  /** Persisted scenarios so scenario-update diff cards can render the real before-state. */
  scenarios?: Array<Record<string, unknown>>
  isDraft: boolean
  /**
   * Optional seed message that auto-sends to Forge when nonce changes.
   * Used by "Discuss with Forge" buttons in the Test tab and on failed
   * scenario rows in Tab 3. Operator lands here with Forge already
   * thinking — no extra click required.
   */
  seedMessage?: string | null
  seedNonce?: number
  /** Called once after the seed has been auto-sent so the parent can clear
      its seedMessage state (prevents re-send on tab remount). */
  onSeedConsumed?: () => void
}

const REGENERATE_PROMPT =
  "Regenerate the proposed changes — tighten them or try a different approach. Keep the chat thread, but propose a fresh set of AI-suggested changes."

type DesignerLogRow = {
  id:             number
  role:           string
  content:        string | null
  toolProposal:   { id?: string; kind?: string; input?: Record<string, unknown> } | null
  proposalNumber: number | null
  createdAt:      string
}

type LogPage = {
  messages:     DesignerLogRow[]
  hasMore:      boolean
  nextBeforeId: number | null
}

const PAGE_SIZE = 50
const SCROLL_TOP_THRESHOLD = 80   // px from top before we trigger an older-page fetch

function partitionRows(rows: DesignerLogRow[]): { turns: Message[]; queued: Proposal[] } {
  const turns: Message[]     = []
  const queued: Proposal[]   = []
  for (const m of rows) {
    if (m.role === "user" || m.role === "assistant") {
      turns.push({ id: m.id, role: m.role, content: m.content ?? "", createdAt: m.createdAt })
    } else if (m.role === "tool_proposal" && m.toolProposal) {
      queued.push({
        id:       m.toolProposal.id ?? `prop-${m.id}`,
        kind:     m.toolProposal.kind ?? "unknown",
        input:    m.toolProposal.input ?? {},
        accepted: true,
        number:   m.proposalNumber ?? undefined,
      })
    }
  }
  return { turns, queued }
}

export function DesignerPane({ skillId, skillSnapshot, scenarios = [], isDraft, seedMessage, seedNonce, onSeedConsumed }: Props) {
  const [messages, setMessages]       = useState<Message[]>([])
  const [draft, setDraft]             = useState("")
  const [streaming, setStreaming]     = useState(false)
  const [proposals, setProposals]     = useState<Proposal[]>([])
  // Right-pane tab. Defaults to "current" so the operator sees the
  // existing skill body on landing. We flip to "proposed" only when a
  // *new* proposal streams in from Forge during this session — old
  // persisted proposals stay behind the tab so the page doesn't
  // ambush the operator on every refresh.
  const [rightTab, setRightTab]       = useState<"current" | "proposed">("current")
  const [applyError, setApplyError]   = useState<string | null>(null)
  const [applying, setApplying]       = useState(false)
  const [hasMoreHistory, setHasMoreHistory] = useState(false)
  const [oldestId, setOldestId]       = useState<number | null>(null)
  const [loadingOlder, setLoadingOlder] = useState(false)
  // Mount-fetch race latch. The seed-message useEffect must NOT call
  // sendText() before the persisted-thread fetch resolves — both run on
  // the same render commit, and `sendText` does an optimistic
  // setMessages([..., user, emptyAssistant]) that gets clobbered when
  // the (slower) fetch resolves and calls setMessages(turns). Symptom
  // pre-fix: user sends a message; SSE deltas append to the wrong
  // (stale) assistant row; refresh shows everything correct because
  // the server persisted fine. Latch flips true on success OR failure
  // so an offline thread fetch doesn't wedge the seed forever.
  const [initialLoaded, setInitialLoaded] = useState(false)
  const threadRef     = useRef<HTMLDivElement | null>(null)
  // Ref-latch for scroll-triggered fetches: closures inside onScroll
  // would otherwise see stale loadingOlder and fire concurrent requests.
  const inFlightRef   = useRef(false)

  // Replay the most-recent page of the persisted thread on mount.
  // Pending tool_proposals from the log re-hydrate the right-side panel.
  useEffect(() => {
    fetch(`/skills/${skillId}/designer_chat?limit=${PAGE_SIZE}`, {
      credentials: "same-origin",
      headers: { Accept: "application/json" },
    })
      .then(async (r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json() as Promise<LogPage> })
      .then((data) => {
        const { turns, queued } = partitionRows(data.messages)
        setMessages(turns)
        setProposals(queued)
        setHasMoreHistory(data.hasMore)
        setOldestId(data.nextBeforeId ?? (turns[0]?.id ?? null))
      })
      .catch(() => {/* leave empty; user can still chat */})
      .finally(() => setInitialLoaded(true))
  }, [skillId])

  // Load the next older page when the operator scrolls near the top.
  // Capture scrollHeight before the fetch and restore scrollTop after
  // prepending so the viewport stays anchored on the message they were
  // reading.
  async function loadOlder() {
    if (inFlightRef.current || !hasMoreHistory || oldestId === null) return
    const el = threadRef.current
    if (!el) return
    inFlightRef.current = true
    setLoadingOlder(true)
    const prevScrollHeight = el.scrollHeight
    try {
      const res = await fetch(`/skills/${skillId}/designer_chat?before_id=${oldestId}&limit=${PAGE_SIZE}`, {
        credentials: "same-origin",
        headers: { Accept: "application/json" },
      })
      if (!res.ok) return
      const data = await res.json() as LogPage
      const { turns: olderTurns, queued: olderQueued } = partitionRows(data.messages)
      setMessages((cur) => [...olderTurns, ...cur])
      // Tool proposals from older history go BELOW current queue (chronological).
      setProposals((cur) => [...olderQueued, ...cur])
      setHasMoreHistory(data.hasMore)
      setOldestId(data.nextBeforeId ?? (olderTurns[0]?.id ?? oldestId))
      // Restore scroll position so prepended rows don't yank the viewport.
      requestAnimationFrame(() => {
        const newEl = threadRef.current
        if (!newEl) return
        newEl.scrollTop = newEl.scrollHeight - prevScrollHeight
      })
    } finally {
      inFlightRef.current = false
      setLoadingOlder(false)
    }
  }

  function onThreadScroll() {
    const el = threadRef.current
    if (!el) return
    if (el.scrollTop <= SCROLL_TOP_THRESHOLD) loadOlder()
  }

  // Auto-scroll thread on new content.
  useEffect(() => {
    threadRef.current?.scrollTo({ top: threadRef.current.scrollHeight, behavior: "smooth" })
  }, [messages.length, streaming])

  // (auto-send-seed effect moved below — it depends on sendText, which
  // depends on streamReply, both declared after this point.)

  const streamReply = useCallback(async (history: Message[]) => {
    setStreaming(true)
    await consumeSSE(
      `/skills/${skillId}/ai_chat`,
      {
        method: "POST",
        body: new URLSearchParams({ messages: JSON.stringify(history) }),
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
      },
      {
        onText: (delta) => {
          setMessages((cur) => {
            const next = [...cur]
            const last = next[next.length - 1]
            if (last && last.role === "assistant") next[next.length - 1] = { ...last, content: last.content + delta }
            return next
          })
        },
        onToolProposal: (raw) => {
          const p = raw as { id?: string; kind?: string; input?: Record<string, unknown>; number?: number }
          setProposals((cur) => [
            ...cur,
            {
              id:       p.id ?? `prop-${Date.now()}-${cur.length}`,
              kind:     p.kind ?? "unknown",
              input:    p.input ?? {},
              accepted: true,
              number:   p.number,
            },
          ])
          // New proposal landing → flip the right pane to the proposed-
          // changes tab so the operator sees it without scrolling.
          setRightTab("proposed")
        },
        onError: (msg) => {
          setMessages((cur) => [
            ...cur,
            { id: Date.now(), role: "assistant", content: `(error: ${msg})`, createdAt: new Date().toISOString() },
          ])
        },
        onEnd: () => {
          setStreaming(false)
        },
      },
    )
    setStreaming(false)
  }, [skillId])

  const sendText = useCallback(async (rawText: string) => {
    const text = rawText.trim()
    if (streaming || !text) return
    const now = new Date().toISOString()
    const newHistory: Message[] = [
      ...messages,
      { id: Date.now(),     role: "user",      content: text, createdAt: now },
      { id: Date.now() + 1, role: "assistant", content: "",   createdAt: now },
    ]
    setMessages(newHistory)
    await streamReply(newHistory.slice(0, -1))
  }, [messages, streaming, streamReply])

  const send = useCallback(async () => {
    if (!draft.trim()) return
    const text = draft
    setDraft("")
    await sendText(text)
  }, [draft, sendText])

  // Auto-send a seeded message to Forge when the parent bumps the nonce
  // (Discuss-with-Forge button). Guarded by lastHandledNonceRef so a
  // remount doesn't replay the seed if the parent hasn't cleared it yet.
  // After firing, we call onSeedConsumed so the parent zeros seedMessage.
  const lastHandledNonceRef = useRef(0)
  useEffect(() => {
    // Wait for the persisted-thread fetch to resolve before optimistic
    // sends — see initialLoaded comment above for the race details.
    if (!initialLoaded) return
    if (!seedMessage || seedMessage.trim().length === 0) return
    if (!seedNonce || seedNonce <= 0) return
    if (lastHandledNonceRef.current === seedNonce) return
    lastHandledNonceRef.current = seedNonce
    sendText(seedMessage)
    onSeedConsumed?.()
  }, [seedNonce, seedMessage, sendText, onSeedConsumed, initialLoaded])

  function toggleProposal(id: string) {
    setProposals((cur) => cur.map((p) => (p.id === id ? { ...p, accepted: !p.accepted } : p)))
  }

  // Helper: drop persisted tool_proposal rows so they don't re-hydrate
  // on reload. Pass an array of proposal ids; pass null to wipe ALL
  // queued proposals for this (skill, user) thread.
  async function deletePersistedProposals(ids: string[] | null) {
    const body = new URLSearchParams()
    if (ids) ids.forEach((id) => body.append("proposal_ids[]", id))
    try {
      await fetch(`/skills/${skillId}/designer_chat/proposals`, {
        method: "DELETE", headers: csrfHeaders(), credentials: "same-origin", body,
      })
    } catch {/* best effort — local state still clears */}
  }

  function removeProposal(id: string) {
    setProposals((cur) => cur.filter((p) => p.id !== id))
    deletePersistedProposals([id])
  }

  async function applySelected() {
    const accepted = proposals.filter((p) => p.accepted)
    if (accepted.length === 0) return
    setApplying(true)
    setApplyError(null)
    try {
      const res = await fetch(`/skills/${skillId}/apply_proposals`, {
        method: "PATCH",
        headers: csrfHeaders(),
        credentials: "same-origin",
        body: new URLSearchParams({ proposals: JSON.stringify(accepted) }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        setApplyError(data.error ?? `HTTP ${res.status}`)
        return
      }
      const appliedIds = new Set(accepted.map((p) => p.id))
      setProposals((cur) => cur.filter((p) => !appliedIds.has(p.id)))
      router.reload({ only: ["skill", "versions"] })
    } finally {
      setApplying(false)
    }
  }

  async function regenerate() {
    if (streaming) return
    const oldIds = proposals.map((p) => p.id)
    setProposals([])
    setApplyError(null)
    if (oldIds.length > 0) await deletePersistedProposals(oldIds)
    const now = new Date().toISOString()
    const newHistory: Message[] = [
      ...messages,
      { id: Date.now(),     role: "user",      content: REGENERATE_PROMPT, createdAt: now },
      { id: Date.now() + 1, role: "assistant", content: "",                createdAt: now },
    ]
    setMessages(newHistory)
    await streamReply(newHistory.slice(0, -1))
  }

  async function cancelQueue() {
    if (proposals.length === 0) return
    if (!confirm(`Discard ${proposals.length} pending change${proposals.length === 1 ? "" : "s"}?`)) return
    const oldIds = proposals.map((p) => p.id)
    setProposals([])
    setApplyError(null)
    await deletePersistedProposals(oldIds)
  }

  // Clear the entire chat history (and any pending proposals). Hits the
  // server to delete persisted rows in skill_designer_messages, then
  // wipes local state.
  async function clearChat() {
    const hasState = messages.length > 0 || proposals.length > 0
    if (hasState && !confirm("Clear the entire chat with Forge, including any pending changes?")) return
    try {
      await fetch(`/skills/${skillId}/designer_chat`, {
        method: "DELETE",
        headers: csrfHeaders(),
        credentials: "same-origin",
      })
    } catch {/* clear locally anyway */}
    setMessages([])
    setProposals([])
    setApplyError(null)
  }

  const acceptedCount  = proposals.filter((p) => p.accepted).length
  const skillVersion   = skillSnapshot.version
  const skillState     = (skillSnapshot.state as string) ?? ""
  const stateTone: "gold" | "red" | "neutral" =
    skillState === "live" ? "gold" : skillState === "draft" ? "red" : "neutral"

  return (
    <div className="flex h-[min(72vh,720px)] flex-col gap-3 lg:flex-row">
      {/* Left: chat thread + composer */}
      <Card className="flex flex-1 min-w-0 flex-col p-0">
        <header className="flex items-center justify-between gap-3 border-b border-border px-4 py-3">
          <div className="flex items-center gap-2">
            <Sparkles size={14} strokeWidth={1.7} className="text-vip-goldSoft" />
            <div>
              <h3 className="text-sm font-semibold">Design with Forge</h3>
              <p className="text-[10px] text-app-subtle">
                Discuss any changes you want to make to your skill — Forge will draft them as diff cards on the right.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              type="button" size="sm" variant="ghost"
              onClick={clearChat}
              disabled={streaming || (messages.length === 0 && proposals.length === 0)}
              title="Clear chat history + pending changes"
            >
              <Eraser size={12} strokeWidth={2} className="mr-1" />
              Clear chat
            </Button>
            <FeedbackButton
              surface="skill_designer"
              size="sm"
              variant="ghost"
              label="Forge Feedback"
              defaultCategory="wrong_answer"
              skillId={skillId}
            />
          </div>
        </header>

        <div
          ref={threadRef}
          onScroll={onThreadScroll}
          className={cn(
            "flex-1 overflow-y-auto px-6 py-6 text-sm",
            !isDraft ? "flex flex-col items-center justify-center" : "space-y-6",
          )}
        >
          {!isDraft ? (
            <div className="flex max-w-md flex-col items-center gap-4">
              <button
                type="button"
                onClick={() => router.post(`/skills/${skillId}/draft_version`)}
                className="rounded-control border border-status-warning/40 bg-status-warning/10 px-6 py-3 text-base font-medium text-status-warning transition hover:bg-status-warning/20 hover:border-status-warning/60"
              >
                Create a Draft First
              </button>
              <div className="rounded-control border border-border bg-white/[0.03] px-3 py-2 text-xs text-app-muted">
                <span className="font-medium text-app-text">Tip — </span>
                If you expect live data interactions for this test, paste a
                {" "}<code className="rounded bg-white/5 px-1 font-mono">source_key</code>{" "}
                (your user id) of someone with the scenario you want to stage,
                and we'll fetch the partner data automatically when drafting
                test scenarios.
              </div>
            </div>
          ) : (
            <>
          {hasMoreHistory && (
            <div className="-mt-3 text-center text-[10px] text-app-subtle">
              {loadingOlder ? "Loading older messages…" : "Scroll up for older messages"}
            </div>
          )}
          {messages.length === 0 && !hasMoreHistory && (
            <>
              <p className="text-xs text-app-muted">
                Ask Forge to shorten the prompt, add a tool, write a new red-team scenario — anything skill-shaped. AI-suggested changes come back as accept/reject diff cards on the right.
              </p>
              <div className="rounded-control border border-border bg-white/[0.03] px-3 py-2 text-xs text-app-muted">
                <span className="font-medium text-app-text">Tip — </span>
                If you expect live data interactions for this test, paste a
                {" "}<code className="rounded bg-white/5 px-1 font-mono">source_key</code>{" "}
                (your user id) of someone with the scenario you want to stage,
                and we'll fetch the partner data automatically when drafting
                test scenarios.
              </div>
            </>
          )}
          {messages.map((m, i) => {
            // Hide the empty assistant placeholder while we wait for the
            // first text delta — the thinking indicator covers that gap.
            const isStreamingPlaceholder = streaming && i === messages.length - 1 && m.role === "assistant" && m.content === ""
            if (isStreamingPlaceholder) return null
            const bubble: ChatBubble = {
              id:         m.id,
              role:       m.role,
              content:    m.content,
              createdAt:  m.createdAt,
              authorName: m.role === "assistant" ? "Forge" : m.role === "user" ? "You" : null,
            }
            return (
              <ChatMessageBubble
                key={m.id}
                msg={bubble}
                userLabel="You"
                assistantLabel="Forge"
              />
            )
          })}
          {streaming && <ChatThinkingIndicator label="Forge" />}
            </>
          )}
        </div>

        <footer className="border-t border-border p-3">
          <div className="flex gap-2">
            <Textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault()
                  send()
                }
              }}
              placeholder={isDraft ? "Ask Forge…" : "Create a draft to chat with Forge"}
              rows={2}
              className="flex-1"
              disabled={streaming || !isDraft}
            />
            <Button
              type="button" variant="red" size="md"
              onClick={send}
              disabled={streaming || !draft.trim() || !isDraft}
              className="self-end"
              aria-label={streaming ? "Forge is thinking" : "Send"}
            >
              {streaming
                ? <Loader2 size={16} strokeWidth={2.5} className="animate-spin" />
                : <Send size={16} strokeWidth={2} />}
            </Button>
          </div>
        </footer>
      </Card>

      {/* Right: tabbed panel. "Current Details" reads the persisted
          skill snapshot; "Proposed changes" lists the diff cards Forge
          streamed back. We auto-flip to "proposed" on every new
          tool_proposal event so a fresh suggestion is never hidden. */}
      <Card className="flex w-full min-w-0 flex-1 flex-col p-0">
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <div className="flex items-center gap-3">
            <div className="flex rounded-control border border-border bg-white/[0.03] p-0.5 text-xs">
              <button
                type="button"
                onClick={() => setRightTab("current")}
                className={cn(
                  "rounded px-3 py-1 transition",
                  rightTab === "current"
                    ? "bg-white/10 text-app-text"
                    : "text-app-muted hover:text-app-text",
                )}
              >
                Current Details
              </button>
              <button
                type="button"
                onClick={() => setRightTab("proposed")}
                className={cn(
                  "rounded px-3 py-1 transition",
                  rightTab === "proposed"
                    ? "bg-white/10 text-app-text"
                    : "text-app-muted hover:text-app-text",
                )}
              >
                Proposed changes{proposals.length > 0 ? ` (${proposals.length})` : ""}
              </button>
            </div>
            <Badge tone={stateTone}>v{String(skillVersion)} {skillState}</Badge>
          </div>
          {rightTab === "proposed" && proposals.length > 0 && (
            <span className="text-[10px] text-app-subtle">{acceptedCount} accepted</span>
          )}
        </div>

        {rightTab === "current" ? (
          <SkillSetupBody skill={skillSnapshot} />
        ) : (
          <>
            <div className="flex-1 space-y-2 overflow-y-auto px-4 py-4">
              {proposals.length === 0 ? (
                <p className="text-xs text-app-muted">
                  AI-suggested changes land here. Ask for a concrete change in the chat to populate this list.
                </p>
              ) : (
                proposals.map((p) => (
                  <ProposalDiffCard
                    key={p.id}
                    proposal={p}
                    onToggle={toggleProposal}
                    onRemove={removeProposal}
                    skillSnapshot={skillSnapshot}
                    scenarios={scenarios}
                  />
                ))
              )}
              {applyError && (
                <p className="rounded-control border border-status-error/30 bg-status-error/10 px-2 py-1 text-xs text-status-error">
                  {applyError}
                </p>
              )}
            </div>

            <footer className="grid grid-cols-3 gap-2 border-t border-border p-3">
              <Button
                type="button" variant="red" size="sm"
                onClick={applySelected}
                disabled={applying || !isDraft || acceptedCount === 0}
              >
                {applying ? "Applying…" : `Apply ${acceptedCount}`}
              </Button>
              <Button
                type="button" variant="ghost" size="sm"
                onClick={regenerate}
                disabled={streaming || messages.length === 0}
                title="Re-prompt Forge to redo the proposals"
              >
                <RefreshCw size={12} strokeWidth={2} className="mr-1" />
                Regenerate
              </Button>
              <Button
                type="button" variant="ghost" size="sm"
                onClick={cancelQueue}
                disabled={proposals.length === 0}
                title="Discard ALL pending changes (use the X on a card to remove just that one)"
              >
                <Trash2 size={12} strokeWidth={2} className="mr-1" />
                Discard all{proposals.length > 0 ? ` (${proposals.length})` : ""}
              </Button>
            </footer>
          </>
        )}
      </Card>
    </div>
  )
}

function csrfHeaders(): HeadersInit {
  const token = document.querySelector<HTMLMetaElement>("meta[name=csrf-token]")?.content
  return {
    "X-CSRF-Token": token ?? "",
    "Content-Type": "application/x-www-form-urlencoded",
    "Accept": "application/json",
    "X-Requested-With": "XMLHttpRequest",
  }
}
